/**
 * MatchScoreTooltip
 * One SPARC portfolio Match badge. Tooltip copy follows the four-band ranker.
 */

import React, { useId, useState, useRef } from 'react';
import { Info } from 'lucide-react';

export interface MatchExplainKeyword {
  keyword: string;
  weight?: number;
  hits?: number;
}

export interface MatchExplain {
  top_theme?: string | null;
  top_keywords?: MatchExplainKeyword[];
  delivery_mode?: {
    label?: string;
    remote_digital?: number;
    in_person_field?: number;
  };
  components?: Record<string, number>;
}

interface MatchScoreTooltipProps {
  score: number; // 0-1
  /** 'badge' = compact coloured pill (card list view)
   *  'detail' = larger display with label (detail modal) */
  variant?: 'badge' | 'detail';
  /** Persisted match_explain from ranking (top keywords, delivery mode). */
  explain?: MatchExplain | null;
}

export function getMatchScoreBandMeta(score: number): {
  label: string;
  colorClass: string;
  headline: string;
  description: string;
} {
  const pct = Math.round(score * 100);

  if (score >= 0.80) {
    return {
      label: `${pct}% Match`,
      colorClass:
        'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-700',
      headline: 'CredX and skill match',
      description:
        'Case study (CredX / past project) and skill set both match. A lone GIS or digital keyword does not reach this band.',
    };
  }
  if (score >= 0.70) {
    return {
      label: `${pct}% Match`,
      colorClass:
        'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-700',
      headline: 'Geospatial scope',
      description:
        'The statement of work is geospatial or GIS delivery, not a stray GIS token in a civil package.',
    };
  }
  if (score >= 0.60) {
    return {
      label: `${pct}% Match`,
      colorClass:
        'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200 border border-amber-200 dark:border-amber-700',
      headline: 'Software delivery',
      description:
        'Regular software or digital delivery, without a CredX case study and without geospatial scope.',
    };
  }
  if (score > 0.30) {
    return {
      label: `${pct}% Match`,
      colorClass:
        'bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 border border-blue-200 dark:border-blue-700',
      headline: 'Partial overlap',
      description:
        'Outside the CredX, geospatial, and software bands, and not civil construction.',
    };
  }
  return {
    label: `${pct}% Match`,
    colorClass:
      'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-600',
    headline: 'Civil / construction',
    description:
      'Construction, roof, wastewater, civil, or unrelated work. Listed and sorted last.',
  };
}

function deliveryLabel(label?: string): string | null {
  switch (label) {
    case 'remote_digital':
      return 'Remote / digital delivery';
    case 'in_person_field':
      return 'In-person / field work';
    case 'hybrid':
      return 'Hybrid delivery';
    default:
      return null;
  }
}

export function MatchScoreTooltip({ score, variant = 'badge', explain }: MatchScoreTooltipProps) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipId = useId();

  const { label, colorClass, headline, description } = getMatchScoreBandMeta(score);
  const topKeywords = (explain?.top_keywords || [])
    .map((k) => (typeof k?.keyword === 'string' ? k.keyword : ''))
    .filter(Boolean)
    .slice(0, 3);
  const delivery = deliveryLabel(explain?.delivery_mode?.label);
  const theme = explain?.top_theme ? String(explain.top_theme).replace(/_/g, ' ') : null;

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  const tooltipContent = (
    <div
      id={tooltipId}
      role="tooltip"
      className="absolute z-50 left-0 top-full"
      style={{ marginTop: '4px' }}
    >
      <div
        className="ml-3"
        style={{
          width: 0,
          height: 0,
          borderLeft: '7px solid transparent',
          borderRight: '7px solid transparent',
          borderBottom: '7px solid #d1d5db',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '1px',
            left: '-6px',
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderBottom: '6px solid white',
          }}
        />
      </div>

      <div className="w-80 rounded-lg shadow-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3 text-left">
        <p className="text-xs font-semibold text-slate-900 dark:text-white mb-1">{headline}</p>
        <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed mb-2">{description}</p>

        {topKeywords.length > 0 && (
          <div className="mb-2">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
              Themes
            </p>
            <div className="flex flex-wrap gap-1">
              {topKeywords.map((kw) => (
                <span
                  key={kw}
                  className="inline-flex px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-xs text-slate-700 dark:text-slate-200"
                >
                  {kw}
                </span>
              ))}
            </div>
          </div>
        )}

        {(delivery || theme) && (
          <div className="mb-2 space-y-0.5">
            {delivery && (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                <span className="font-medium">Delivery: </span>
                {delivery}
              </p>
            )}
            {theme && (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                <span className="font-medium">Theme: </span>
                {theme}
              </p>
            )}
          </div>
        )}

        <div className="border-t border-slate-100 dark:border-slate-700 pt-2">
          <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
            <span className="font-medium">How it is calculated: </span>
            One SPARC portfolio score. CredX plus skill set lands 80 to 85 percent. Real geospatial
            scope lands 70 to 75. Regular software lands 60 to 65. Civil construction is 30 percent
            or below. Theme names are labels, not a second score.
          </p>
        </div>
      </div>
    </div>
  );

  if (variant === 'detail') {
    return (
      <div
        className="relative inline-flex flex-col items-start gap-1 cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-md"
        tabIndex={0}
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
      >
        <div className="inline-flex items-center gap-1.5">
          <span
            className={`text-sm font-semibold tabular-nums px-2 py-0.5 rounded ${colorClass}`}
          >
            {label}
          </span>
          <Info className="h-3.5 w-3.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300" aria-hidden="true" />
        </div>
        {topKeywords.length > 0 && (
          <div className="flex flex-wrap gap-1 max-w-xs">
            {topKeywords.map((kw) => (
              <span
                key={kw}
                className="inline-flex px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-[10px] text-slate-600 dark:text-slate-300"
              >
                {kw}
              </span>
            ))}
          </div>
        )}
        {open && tooltipContent}
      </div>
    );
  }

  return (
    <div className="relative inline-flex flex-col items-start gap-1">
      <div
        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold tabular-nums cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${colorClass}`}
        tabIndex={0}
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
      >
        {label}
        <Info className="h-3 w-3 opacity-60" aria-hidden="true" />
        {open && tooltipContent}
      </div>
    </div>
  );
}
